import * as Sentry from '@sentry/react-native';

type MobileExceptionTags = Record<string, string>;

const getEnvValue = (value: string | undefined) => value?.trim() || undefined;

/* eslint-disable dot-notation -- Keep literal env keys inlineable while satisfying noPropertyAccessFromIndexSignature. */
const getSentryEnvironment = () =>
  getEnvValue(process.env['DOPPLER_CONFIG']) ||
  getEnvValue(process.env['DOPPLER_ENVIRONMENT']) ||
  getEnvValue(process.env['APP_ENV']) ||
  getEnvValue(process.env['NX_TASK_TARGET_CONFIGURATION']) ||
  'production';
/* eslint-enable dot-notation */

const isServerError = (error: unknown) => {
  if (!error || typeof error !== 'object' || !('response' in error)) {
    return false;
  }

  const response = (error as { response?: unknown }).response;

  if (!response || typeof response !== 'object' || !('status' in response)) {
    return false;
  }

  const status = (response as { status?: unknown }).status;

  return typeof status === 'number' && status >= 500 && status < 600;
};

const captureMobileException = (
  error: unknown,
  metadata: MobileExceptionTags = {},
) => {
  Sentry.captureException(error, {
    tags: {
      platform: 'mobile',
      ...metadata,
    },
  });
};

const setupSentry = () => {
  Sentry.init({
    // disable sentry in development
    enabled: !__DEV__,

    // eslint-disable-next-line dot-notation, @typescript-eslint/no-explicit-any
    dsn: (process.env as any)['SENTRY_DSN'],

    environment: getSentryEnvironment(),

    // Set tracesSampleRate to 1.0 to capture 100% of transactions for performance monitoring.
    // We recommend adjusting this value in production.
    tracesSampleRate: 1.0,

    // Set the replaySampleRate to 0.1 to capture 10% of sessions.
    replaysSessionSampleRate: 0.1,

    // Set the replayonErrorSampleRate to 1.0 to capture 100% of errors sessions.
    replaysOnErrorSampleRate: 1.0,

    integrations: [Sentry.mobileReplayIntegration()],
  });
};

export {
  setupSentry,
  getSentryEnvironment,
  isServerError,
  captureMobileException,
};
